import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Megaphone, Plus, Trash, X } from '@phosphor-icons/react';
import { useAuth } from '../App';
import { apiPath } from '../config/apiBase';
import UserPicker from './UserPicker';
import { ModuleShell, SkeletonBlock } from './module-ui';
import { notifySuccess, notifyError } from '../lib/notify';

const API = apiPath('/api/announcements');
const PRODUCTS_API = apiPath('/api/products');

function authHeaders() {
  const token = localStorage.getItem('token');
  return { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
}

function Banner({ children, ok, warn }) {
  const bg = ok ? '#ECFDF5' : warn ? '#FFFBEB' : '#FEF2F2';
  const color = ok ? '#065F46' : warn ? '#92400E' : '#991B1B';
  const border = ok ? '#A7F3D0' : warn ? '#FDE68A' : '#FECACA';
  return (
    <div style={{
      background: bg, color, border: `1px solid ${border}`,
      borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '13px',
    }}
    >
      {children}
    </div>
  );
}

function audienceLabel(row, productMap) {
  const aud = row.audience || 'all';
  if (aud === 'all') return 'Everyone';
  if (aud === 'products') {
    const names = (row.product_ids || []).map((id) => productMap[id] || id);
    return names.length ? `Products: ${names.join(', ')}` : 'Products: —';
  }
  const n = (row.user_ids || []).length;
  return n ? `Users: ${n} selected` : 'Users: —';
}

export default function AnnouncementsAdminPage() {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('Announcement');
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState('all');
  const [productIds, setProductIds] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const productMap = Object.fromEntries(products.map((p) => [p.product_id, p.name]));

  const load = async () => {
    setLoading(true);
    try {
      const [a, p] = await Promise.all([
        axios.get(API, authHeaders()),
        axios.get(PRODUCTS_API, authHeaders()).catch(() => ({ data: [] })),
      ]);
      setRows(a.data || []);
      setProducts(p.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  const flash = (m) => {
    notifySuccess(m);
    setError(null);
  };

  const toggleProduct = (id) => {
    setProductIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const addUser = (u) => {
    if (!u?.user_id) return;
    setSelectedUsers((prev) => (
      prev.some((x) => x.user_id === u.user_id) ? prev : [...prev, u]
    ));
  };

  const removeUser = (userId) => {
    setSelectedUsers((prev) => prev.filter((u) => u.user_id !== userId));
  };

  const send = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      setError('Message is required');
      return;
    }
    if (audience === 'products' && productIds.length === 0) {
      setError('Select at least one product');
      return;
    }
    if (audience === 'users' && selectedUsers.length === 0) {
      setError('Select at least one user');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await axios.post(
        API,
        {
          title: title.trim() || 'Announcement',
          message: message.trim(),
          audience,
          product_ids: audience === 'products' ? productIds : [],
          user_ids: audience === 'users' ? selectedUsers.map((u) => u.user_id) : [],
        },
        authHeaders(),
      );
      setMessage('');
      setTitle('Announcement');
      setAudience('all');
      setProductIds([]);
      setSelectedUsers([]);
      flash('Announcement sent — matching users will see it once on their dashboard');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await axios.patch(
        `${API}/${row.announcement_id}`,
        { active: !row.active },
        authHeaders(),
      );
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Update failed');
    }
  };

  const remove = async (row) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API}/${row.announcement_id}`, authHeaders());
      flash('Deleted');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  if (!isAdmin) {
    return <p style={{ color: '#6B7280' }}>Only Admin or Super Admin can manage announcements.</p>;
  }

  const radioStyle = { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' };

  return (
    <ModuleShell
      title="Announcements"
      icon={Megaphone}
      subtitle="Send a popup to everyone, selected products' teams, or specific users. They close it with the X — it will not show again for that user."
    >
    <div style={{ maxWidth: '820px' }}>
      {error && <Banner>{typeof error === 'string' ? error : JSON.stringify(error)}</Banner>}

      <form
        onSubmit={send}
        style={{
          background: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '10px',
          padding: '16px',
          marginBottom: '22px',
        }}
      >
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>
          Title
        </label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          style={{
            width: '100%',
            padding: '8px 10px',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            fontSize: '13px',
            marginBottom: '12px',
            boxSizing: 'border-box',
          }}
        />
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>
          Message
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          maxLength={4000}
          placeholder="Write the announcement users will see…"
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            fontSize: '13px',
            resize: 'vertical',
            marginBottom: '14px',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
          }}
        />

        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>
          Send to
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '12px' }}>
          <label style={radioStyle}>
            <input type="radio" checked={audience === 'all'} onChange={() => setAudience('all')} />
            Everyone
          </label>
          <label style={radioStyle}>
            <input type="radio" checked={audience === 'products'} onChange={() => setAudience('products')} />
            Selected products
          </label>
          <label style={radioStyle}>
            <input type="radio" checked={audience === 'users'} onChange={() => setAudience('users')} />
            Selected users
          </label>
        </div>

        {audience === 'products' && (
          <div style={{
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '12px',
            maxHeight: '180px',
            overflowY: 'auto',
          }}
          >
            {products.length === 0 ? (
              <span style={{ color: '#9CA3AF', fontSize: '12px' }}>No products found</span>
            ) : (
              products.map((p) => (
                <label
                  key={p.product_id}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '6px', cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={productIds.includes(p.product_id)}
                    onChange={() => toggleProduct(p.product_id)}
                  />
                  {p.name}
                </label>
              ))
            )}
          </div>
        )}

        {audience === 'users' && (
          <div style={{ marginBottom: '12px' }}>
            <UserPicker
              staffOnly={false}
              placeholder="Search and add users…"
              onSelect={addUser}
            />
            {selectedUsers.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                {selectedUsers.map((u) => (
                  <span
                    key={u.user_id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: '#EEF0FF',
                      border: '1px solid #C7D2FE',
                      borderRadius: '999px',
                      padding: '4px 10px',
                      fontSize: '12px',
                    }}
                  >
                    {u.name || u.email || u.user_id}
                    <button
                      type="button"
                      onClick={() => removeUser(u.user_id)}
                      aria-label="Remove"
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, display: 'flex' }}
                    >
                      <X size={14} color="#4338CA" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: '#002FA7',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 16px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          <Plus size={16} weight="bold" />
          {busy ? 'Sending…' : 'Send announcement'}
        </button>
      </form>

      <h2 style={{ fontSize: '13px', fontWeight: 700, margin: '0 0 10px' }}>Recent announcements</h2>
      {loading ? (
        <p style={{ color: '#6B7280', fontSize: '13px' }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: '#6B7280', fontSize: '13px' }}>No announcements yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {rows.map((row) => (
            <div
              key={row.announcement_id}
              style={{
                border: '1px solid #E5E7EB',
                borderRadius: '10px',
                padding: '12px 14px',
                background: row.active ? '#fff' : '#F9FAFB',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '13px' }}>{row.title}</div>
                  <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
                    {row.created_by_name || row.created_by}
                    {' · '}
                    {row.created_at ? new Date(row.created_at).toLocaleString() : ''}
                    {' · '}
                    <span style={{ color: row.active ? '#059669' : '#9CA3AF' }}>
                      {row.active ? 'Active' : 'Inactive'}
                    </span>
                    {' · '}
                    {audienceLabel(row, productMap)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => toggleActive(row)}
                    style={{
                      border: '1px solid #D1D5DB',
                      background: '#fff',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                  >
                    {row.active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(row)}
                    aria-label="Delete"
                    style={{
                      border: '1px solid #FECACA',
                      background: '#FEF2F2',
                      borderRadius: '6px',
                      padding: '6px 8px',
                      cursor: 'pointer',
                      display: 'flex',
                    }}
                  >
                    <Trash size={16} color="#B91C1C" />
                  </button>
                </div>
              </div>
              <div style={{ fontSize: '13px', color: '#374151', whiteSpace: 'pre-wrap' }}>{row.message}</div>
            </div>
          ))}
        </div>
      )}
    </div>
    </ModuleShell>
  );
}
